import { Hourglass } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { StalledCandidateEntry } from '@hygge/shared';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useStalledCandidates } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';
import { Chip } from '../chip';
import { candidateInitials } from '../widget-format';

function StalledRow({ c }: { c: StalledCandidateEntry }) {
  return (
    <Link
      to="/recruitment/candidates/$id"
      params={{ id: c.candidateId }}
      className="dashboard-row group flex items-center gap-3 border-b border-border py-2.5 last:border-0"
    >
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-xs">{candidateInitials(c.candidateName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium group-hover:underline">{c.candidateName}</div>
        <div className="truncate text-xs text-muted-foreground">
          {c.vacancy} · {c.stage}
        </div>
      </div>
      <Chip color={c.daysStalled >= 14 ? 'red' : 'amber'}>
        {c.daysStalled} day{c.daysStalled === 1 ? '' : 's'}
      </Chip>
    </Link>
  );
}

/**
 * `stalledCandidates` widget — candidates in the HIRING bucket whose stage
 * hasn't changed for 7+ days (recruiter → own; lead/super_admin → all), longest-stalled
 * first. Each row links to the candidate page.
 */
export function StalledCandidatesWidget() {
  const { data, isLoading, isError } = useStalledCandidates();
  const rows = data ?? [];

  return (
    <WidgetCard
      title="Stalled Candidates"
      icon={Hourglass}
      caption="In the Hiring stage with no change for 7+ days."
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-28" />
              </div>
              <Skeleton className="h-4 w-10" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load stalled candidates.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          No stalled candidates — nice work.
        </p>
      ) : (
        <ExpandableList
          items={rows}
          getKey={(c) => c.id}
          renderItem={(c: StalledCandidateEntry) => <StalledRow c={c} />}
        />
      )}
    </WidgetCard>
  );
}
